import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Container, Typography, CssBaseline } from '@mui/material';

import GroupsIcon from "@mui/icons-material/Groups";
import { ThemeProvider as CustomThemeProvider, useTheme } from "../contexts/ThemeContext";
import { useSystemButtonStyles } from "../hooks/useSystemButtonStyles";
import AuthPageToggleBar from "../components/AuthPageToggleBar";
import { fetchPublicAnmalningLanding } from "../utils/api/api";
import {
  buildAnmalningApplyLink,
  getAnmalningTokenFromSearch,
} from "../utils/anmalningPublicLink";
import useAnmalningLinkLanguage from "../hooks/useAnmalningLinkLanguage";
import {
  REGISTRATION_MODE_LABEL_KEYS,
  registrationModeAllowsSignup,
} from "../constants/anmalningarRegistrationModes";
import { authCardShellSx, authPageOuterSx } from "../utils/authPageStyles";
import { APP_FONT_FAMILY } from "../constants/typography";
import newEraLogo from "../NewEraLogo.png";
import { getMm } from "../theme/modernMaterialEnterprise";

const LOGO_LIGHT = `${process.env.PUBLIC_URL || ""}/NewEraBlackLogo.png`;
const FONT = APP_FONT_FAMILY;

function AnmalningLandingContent() {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const { primaryButtonSx } = useSystemButtonStyles();
  const location = useLocation();
  const token = useMemo(() => getAnmalningTokenFromSearch(location.search), [location.search]);
  const linkLang = useAnmalningLinkLanguage(location.search);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [landing, setLanding] = useState(null);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError(t("anmalningar_landing_missing_token"));
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const data = await fetchPublicAnmalningLanding(token);
        if (!cancelled) setLanding(data);
      } catch (err) {
        if (cancelled) return;
        const code = err?.response?.data?.code;
        if (code === "anmalning_link_invalid") {
          setError(t("anmalningar_landing_invalid_link"));
        } else if (code === "anmalning_link_not_configured") {
          setError(t("anmalningar_link_not_configured"));
        } else {
          setError(t("anmalningar_landing_load_failed"));
        }
        setLanding(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, t]);

  const cardShellSx = useMemo(() => authCardShellSx(isDarkMode), [isDarkMode]);

  return (
    <Box sx={authPageOuterSx(isDarkMode)}>
      <CssBaseline />
      <AuthPageToggleBar />
      <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 5 }, px: { xs: 2, sm: 4 } }}>
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Box
            component="img"
            src={isDarkMode ? newEraLogo : LOGO_LIGHT}
            alt="NewEra"
            sx={{ height: 40, mb: 2 }}
          />
          <Typography
            sx={{
              fontFamily: FONT,
              fontWeight: 800,
              fontSize: { xs: "1.5rem", sm: "1.875rem" },
              color: getMm(isDarkMode).onSurface,
              mb: 0.5,
            }}
          >
            {t("anmalningar_landing_title")}
          </Typography>
          {landing?.clubname ? (
            <Typography
              sx={{
                fontFamily: FONT,
                fontWeight: 600,
                fontSize: { xs: "1rem", sm: "1.125rem" },
                color: getMm(isDarkMode).onSurfaceVariant,
              }}
            >
              {landing.clubname}
            </Typography>
          ) : null}
          <Typography
            sx={{
              mt: 1,
              fontFamily: FONT,
              color: getMm(isDarkMode).onSurfaceVariant,
              fontSize: "0.9375rem",
            }}
          >
            {t("anmalningar_landing_subtitle")}
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : null}

        {!loading && error ? (
          <Alert severity="error" sx={{ borderRadius: "8px", mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {!loading && !error && landing ? (
          <>
            {landing.groups?.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: "8px" }}>
                {t("anmalningar_landing_no_groups")}
              </Alert>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {landing.groups.map((group) => {
                  const canSignup = registrationModeAllowsSignup(group.registrationMode);
                  return (
                    <Card key={group.groupID} elevation={0} sx={cardShellSx}>
                      <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            gap: 1,
                            mb: 1.5,
                            flexWrap: "wrap",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <GroupsIcon sx={{ color: getMm(isDarkMode).onSurfaceVariant }} />
                            <Typography
                              sx={{
                                fontFamily: FONT,
                                fontWeight: 700,
                                fontSize: "1.0625rem",
                                color: getMm(isDarkMode).onSurface,
                              }}
                            >
                              {group.groupName}
                            </Typography>
                          </Box>
                          <Chip
                            size="small"
                            label={t(REGISTRATION_MODE_LABEL_KEYS[group.registrationMode] || "anmalningar_mode_none")}
                            sx={{ fontFamily: FONT, fontWeight: 600 }}
                          />
                        </Box>

                        {group.registrationInfoBefore ? (
                          <Typography
                            sx={{
                              whiteSpace: "pre-wrap",
                              fontFamily: FONT,
                              fontSize: "0.9375rem",
                              lineHeight: 1.6,
                              color: getMm(isDarkMode).onSurfaceVariant,
                              mb: 2,
                            }}
                          >
                            {group.registrationInfoBefore}
                          </Typography>
                        ) : null}

                        {canSignup ? (
                          <Button
                            component={RouterLink}
                            to={buildAnmalningApplyLink({
                              token,
                              groupId: group.groupID,
                              lang: linkLang,
                              relative: true,
                            })}
                            variant="contained"
                            sx={{ textTransform: "none", fontWeight: 700, ...primaryButtonSx }}
                          >
                            {t("anmalningar_landing_apply")}
                          </Button>
                        ) : (
                          <Typography variant="body2" sx={{ color: getMm(isDarkMode).onSurfaceVariant }}>
                            {t("anmalningar_landing_info_only_hint")}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            )}
          </>
        ) : null}
      </Container>
    </Box>
  );
}

export default function AnmalningLandingPage() {
  return (
    <CustomThemeProvider>
      <AnmalningLandingContent />
    </CustomThemeProvider>
  );
}
